import { useEffect } from 'react';
import { Updater } from 'use-immer';
import ExportHistoryButton from 'src/components/Buttons/ExportHistoryButton';
import {
	DatePicker,
	DateRange,
	useDatePickerStates,
} from 'src/components/DatePicker/DatePicker';
import { DEFAULT_VALIDATION_CONFIG } from 'src/constants/historyTables';
import { cj } from 'src/utils/classNames';
import { WithChildren } from 'src/utils/UtilityTypes';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import { FilterableHistoryType } from 'src/@types/historyTables';
import { DownloadRecordType } from '@drift/common';

interface Props extends WithChildren {
	historyType?: Exclude<FilterableHistoryType, 'orders' | 'predictionTrades'>;
	exportType?: DownloadRecordType;
	noExportButton?: boolean;
}

export default function PortfolioHistoryTableControls({
	historyType,
	exportType,
	noExportButton,
	children,
}: Props) {
	const filterState = useHistoryTableFilterStore((s) =>
		historyType ? s.getFilterState(historyType) : undefined
	);
	const setHistoryTableFilterState = useHistoryTableFilterStore((s) => s.set);

	const { dateRange, setDateRange } = useDatePickerStates(
		filterState
			? {
					startDate: filterState.startDate ?? filterState.defaultStartDate,
					endDate: filterState.endDate ?? filterState.defaultEndDate,
				}
			: undefined
	);

	// Reset the filter state when the component is first rendered
	useEffect(() => {
		if (!historyType) {
			return;
		}

		setHistoryTableFilterState((s) => {
			s[historyType].startDate = null;
			s[historyType].endDate = null;
			s[historyType].currentUiPage = 0;
		});
	}, [historyType]);

	// Update the filter state whenever the selected date range changes
	useEffect(() => {
		if (!historyType) {
			return;
		}

		setHistoryTableFilterState((s) => {
			s[historyType].startDate = dateRange.startDate.startOf('day');
			s[historyType].endDate = dateRange.endDate.endOf('day');
			s[historyType].currentUiPage = 0;
		});
	}, [dateRange, historyType]);

	return (
		<div
			className={cj(
				'flex items-center gap-sm rounded-t-sm pb-md sm:bg-container-bg sm:p-md',
				'overflow-hidden border-stroke-divider sm:border-b',
				historyType && 'not-sm:flex-col'
			)}
		>
			{children && (
				<div className="flex gap-sm not-sm:w-full not-sm:flex-col sm:items-center">
					{children}
				</div>
			)}
			<div className="flex w-full items-center justify-between gap-sm not-sm:flex-wrap sm:flex-1">
				{historyType && (
					<DatePicker
						dateRange={dateRange}
						setDateRange={
							setDateRange as Updater<Pick<DateRange, 'startDate' | 'endDate'>>
						}
						validationConfig={DEFAULT_VALIDATION_CONFIG}
						customMessage={
							'You can only view 90 days worth of account history. Your full history is available via export.'
						}
					/>
				)}
				{!noExportButton && (
					<ExportHistoryButton
						className={cj(!historyType && 'ml-auto')}
						type={exportType}
					/>
				)}
			</div>
		</div>
	);
}
